/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from "react";

const C = { orange:"#F28705", green:"#4ade80", blue:"#60a5fa", red:"#f87171", bg:"#0a0a0a", bg2:"#161616", bg3:"#101010", border:"#2c2c2c", text:"#f0ede6", text3:"#8a8780", text4:"#5a5754" };

const ASSETS = [
  { ticker:"BTC",  yahooTicker:"BTC-USD",  name:"Bitcoin",   color:"#F7931A", icon:"₿" },
  { ticker:"ETH",  yahooTicker:"ETH-USD",  name:"Ethereum",  color:"#627EEA", icon:"Ξ" },
  { ticker:"SOL",  yahooTicker:"SOL-USD",  name:"Solana",    color:"#9945FF", icon:"◎" },
  { ticker:"MSTR", yahooTicker:"MSTR",     name:"Strategy",  color:"#F28705", icon:"M" },
  { ticker:"NVDA", yahooTicker:"NVDA",     name:"NVIDIA",    color:"#76B900", icon:"N" },
  { ticker:"AAPL", yahooTicker:"AAPL",     name:"Apple",     color:"#A2AAAD", icon:"" },
  { ticker:"CUSTOM", yahooTicker:null,     name:"Custom",    color:"#F28705", icon:"★" },
];

const LABELS = ["NEW ATH 🚀", "TARGET HIT 🎯", "MILESTONE 💎", "MOON 🌕", "LFG 🔥"];

function fmt$(v) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

function fmtFull(v) {
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Easing
function easeInOutCubic(t) { return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2,3)/2; }
function easeOutExpo(t)     { return t === 1 ? 1 : 1 - Math.pow(2, -10*t); }
// Elastic: overshoots to 1.2 then springs back to 1.0
function easeOutElastic(t) {
  if (t === 0 || t === 1) return t;
  const c4 = (2 * Math.PI) / 3;
  return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
}

// ── Canvas Renderer ────────────────────────────────────────────────────────────
function drawFrame(ctx, state, config) {
  const { W, H } = config;
  const { progress, counterValue, sparkPoints, particles, labelScale, labelOpacity, glowOpacity,
          candles = [], candleAll = null, candleFloatIdx = candles.length, lastCandlePct = 1,
          lineRevealPct = 1, sparkAll: allPointsForLine = null } = state;

  // Use full candle set for scale so chart doesn't jump as candles appear
  const scaleCandles = (candleAll && candleAll.length > 1) ? candleAll : candles;

  ctx.clearRect(0, 0, W, H);

  // Background
  ctx.fillStyle = config.bgColor || "#0a0a0a";
  ctx.fillRect(0, 0, W, H);

  // Subtle grid
  ctx.strokeStyle = "rgba(255,255,255,0.03)";
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 80) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = 0; y < H; y += 80) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

  // Header image banner (130px tall)
  const HEADER_H = 130;
  const headerImg = config.headerImg?.current;
  if (headerImg) {
    const iW = headerImg.naturalWidth, iH = headerImg.naturalHeight;
    const scale = Math.max(W / iW, HEADER_H / iH);
    const dW = iW * scale, dH = iH * scale;
    const ox = (W - dW) / 2, oy = (HEADER_H - dH) / 2;
    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, W, HEADER_H); ctx.clip();
    ctx.drawImage(headerImg, ox, oy, dW, dH);
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.fillRect(0, 0, W, HEADER_H);
    ctx.restore();
  } else {
    const hGrad = ctx.createLinearGradient(0, 0, 0, HEADER_H);
    hGrad.addColorStop(0, `${config.assetColor}22`);
    hGrad.addColorStop(1, "transparent");
    ctx.fillStyle = hGrad;
    ctx.fillRect(0, 0, W, HEADER_H);
  }
  // Header separator line
  ctx.strokeStyle = `${config.assetColor}88`;
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(0, HEADER_H); ctx.lineTo(W, HEADER_H); ctx.stroke();

  // Compaundia logo — top left
  const logoX = 44, logoY = HEADER_H / 2;
  ctx.beginPath(); ctx.arc(logoX, logoY, 9, 0, Math.PI * 2);
  ctx.fillStyle = "#F28705"; ctx.fill();
  ctx.font = `800 28px Montserrat, Arial`;
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "left"; ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0,0,0,0.7)"; ctx.shadowBlur = 8;
  ctx.fillText("compaundia", logoX + 18, logoY);
  ctx.shadowBlur = 0;

  // Glow blob
  if (glowOpacity > 0) {
    const grad = ctx.createRadialGradient(W/2, H/2, 0, W/2, H/2, 320);
    grad.addColorStop(0, `${config.assetColor}${Math.round(glowOpacity * 40).toString(16).padStart(2,"0")}`);
    grad.addColorStop(1, "transparent");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  }

  // Vertical offset for all content
  const OY = HEADER_H;

  // Sparkline chart — positioned to fit within H=800
  const chartX = 80, chartY = OY + 390, chartW = W - 160, chartH = 220;

  if (config.chartMode === "candles" && candles.length > 0) {
    // Scale from ALL candles so chart doesn't jump
    const allPrices = scaleCandles.flatMap(c => [c.h, c.l]);
    const minV  = Math.min(...allPrices);
    const maxV  = Math.max(...allPrices);
    const range = maxV - minV || 1;
    const toY   = (v) => chartY + chartH - ((v - minV) / range) * chartH;

    // Width based on total candle count for stable layout
    const totalCount = scaleCandles.length;
    const candleGap  = chartW / totalCount;
    const candleW    = Math.max(3, Math.floor(candleGap * 0.65));

    candles.forEach((c, i) => {
      const candleProgress = Math.max(0, Math.min(1, candleFloatIdx - i));
      if (candleProgress === 0) return;

      // Elastic scale — overshoots to ~120% then settles at 100%
      const eased   = easeOutElastic(candleProgress);
      const isUp    = c.c >= c.o;
      const color   = isUp ? "#4ade80" : "#f87171";
      const cx      = chartX + i * candleGap + candleGap / 2;

      const bodyTop = toY(Math.max(c.o, c.c));
      const bodyBot = toY(Math.min(c.o, c.c));
      const bodyH   = Math.max(2, bodyBot - bodyTop);
      const midY    = (bodyTop + bodyBot) / 2;
      const wickTop = toY(c.h);
      const wickBot = toY(c.l);

      // Scale body and wick height by elastic factor from midY
      const scaleH      = eased; // goes 0 → 1.2 → 1.0
      const animBodyH   = bodyH * scaleH;
      const animBodyTop = midY - animBodyH / 2;

      // Wick also scales from midY
      const wickHalfUp  = (midY - wickTop) * scaleH;
      const wickHalfDn  = (wickBot - midY) * scaleH;

      ctx.strokeStyle = color;
      ctx.lineWidth   = Math.max(1, candleW * 0.2);
      ctx.beginPath();
      ctx.moveTo(cx, midY - wickHalfUp);
      ctx.lineTo(cx, midY + wickHalfDn);
      ctx.stroke();

      ctx.fillStyle = color;
      ctx.fillRect(cx - candleW / 2, animBodyTop, candleW, Math.max(1, animBodyH));
    });

  } else {
    // ── Line chart — clip reveal from left to right ───────────────────────────
    // Use full sparkAll for the complete path, sparkPoints for the visible dot
    const fullPoints = allPointsForLine && allPointsForLine.length > 1 ? allPointsForLine : sparkPoints;
    if (fullPoints.length > 1) {
      const minV = Math.min(...fullPoints);
      const maxV = Math.max(...fullPoints);
      const range = maxV - minV || 1;
      const toX = (i) => chartX + (i / (fullPoints.length - 1)) * chartW;
      const toY = (v) => chartY + chartH - ((v - minV) / range) * chartH;

      const isUp      = fullPoints[fullPoints.length - 1] >= fullPoints[0];
      const lineColor = isUp ? "#4ade80" : "#f87171";

      // Reveal width — how far right the line is shown
      const revealW = chartW * lineRevealPct;
      const revealX = chartX + revealW;

      ctx.save();
      // Clip: only draw within the revealed area
      ctx.beginPath();
      ctx.rect(chartX, chartY - 20, revealW, chartH + 40);
      ctx.clip();

      // Area fill (full path, clipped)
      const areaGrad = ctx.createLinearGradient(0, chartY, 0, chartY + chartH);
      areaGrad.addColorStop(0, `${lineColor}33`);
      areaGrad.addColorStop(1, `${lineColor}00`);
      ctx.beginPath();
      ctx.moveTo(toX(0), chartY + chartH);
      fullPoints.forEach((v, i) => ctx.lineTo(toX(i), toY(v)));
      ctx.lineTo(toX(fullPoints.length - 1), chartY + chartH);
      ctx.closePath();
      ctx.fillStyle = areaGrad; ctx.fill();

      // Line (full path, clipped)
      ctx.beginPath();
      fullPoints.forEach((v, i) => i === 0 ? ctx.moveTo(toX(i), toY(v)) : ctx.lineTo(toX(i), toY(v)));
      ctx.strokeStyle = lineColor; ctx.lineWidth = 3;
      ctx.lineJoin = "round"; ctx.lineCap = "round"; ctx.stroke();

      ctx.restore();

      // Dot at the reveal front edge
      const dotIdx  = Math.min(fullPoints.length - 1, Math.floor(lineRevealPct * (fullPoints.length - 1)));
      const dotX    = toX(dotIdx);
      const dotY    = toY(fullPoints[dotIdx]);
      ctx.beginPath(); ctx.arc(dotX, dotY, 6, 0, Math.PI * 2);
      ctx.fillStyle = lineColor; ctx.fill();
      ctx.beginPath(); ctx.arc(dotX, dotY, 10, 0, Math.PI * 2);
      ctx.fillStyle = `${lineColor}44`; ctx.fill();
    }
  }

  // Headline
  const headlineY = OY + 60;
  ctx.font = `900 56px Montserrat, Arial`;
  ctx.fillStyle = config.headlineColor || "#ffffff";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0,0,0,0.5)"; ctx.shadowBlur = 10;
  ctx.fillText(config.assetName, W/2, headlineY);
  ctx.shadowBlur = 0;
  ctx.font = `700 30px Montserrat, Arial`;
  ctx.fillStyle = config.subtextColor || config.assetColor;
  ctx.fillText(config.headlineText, W/2, headlineY + 54);

  // Main counter
  const counterY = OY + 210;
  ctx.font = `900 ${counterValue >= 100000 ? 80 : 92}px Montserrat, Arial`;
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.shadowColor = config.assetColor;
  ctx.shadowBlur = 30 * glowOpacity;
  ctx.fillStyle = config.counterColor || "#ffffff";
  ctx.fillText(fmtFull(counterValue), W/2, counterY);
  ctx.shadowBlur = 0;

  // +% change
  if (progress > 0.1) {
    const changePct = ((config.endPrice - config.startPrice) / config.startPrice * 100).toFixed(2);
    const changeAlpha = Math.min((progress - 0.1) / 0.2, 1);
    ctx.font = `700 24px Montserrat, Arial`;
    ctx.fillStyle = `rgba(74,222,128,${changeAlpha})`;
    ctx.textAlign = "center";
    ctx.fillText(`+${changePct}%`, W/2, counterY + 60);
  }

  // Milestone badge
  if (labelOpacity > 0) {
    ctx.save();
    ctx.globalAlpha = labelOpacity;
    ctx.translate(W/2, OY + 330);
    ctx.scale(labelScale, labelScale);
    const labelW = 300, labelH = 48;
    ctx.beginPath(); ctx.roundRect(-labelW/2, -labelH/2, labelW, labelH, 24);
    ctx.fillStyle = config.assetColor; ctx.fill();
    ctx.font = `900 20px Montserrat, Arial`;
    ctx.fillStyle = "#000";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(config.label, 0, 1);
    ctx.restore();
  }

  // Particles
  particles.forEach(p => {
    ctx.globalAlpha = p.opacity;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fillStyle = p.color; ctx.fill();
  });
  ctx.globalAlpha = 1;

  // Bottom watermark
  ctx.font = `600 16px Montserrat, Arial`;
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  ctx.textAlign = "center";
  ctx.fillText("compaundia.com", W/2, H - 28);
}


// ── Main Component ─────────────────────────────────────────────────────────────
export default function PriceAnimationStudio() {
  const [selectedAsset, setSelectedAsset] = useState(ASSETS[0]);
  const [customTicker,  setCustomTicker]  = useState("CUSTOM");
  const [customColor,   setCustomColor]   = useState("#F28705");
  const [customIcon,    setCustomIcon]    = useState("★");
  const [startPrice,    setStartPrice]    = useState(74000);
  const [endPrice,      setEndPrice]      = useState(75000);
  const [label,         setLabel]         = useState("MILESTONE 💎");
  const [headlineText,  setHeadlineText]  = useState("just reached");
  const [duration,      setDuration]      = useState(6);
  const [isPlaying,     setIsPlaying]     = useState(false);
  const [isRecording,   setIsRecording]   = useState(false);
  const [progress,      setProgress]      = useState(0);

  // Colors
  const [bgColor,        setBgColor]       = useState("#0a0a0a");
  const [headlineColor,  setHeadlineColor] = useState("#ffffff");
  const [subtextColor,   setSubtextColor]  = useState("#F28705");
  const [counterColor,   setCounterColor]  = useState("#ffffff");

  // Header image
  const [headerImageSrc, setHeaderImageSrc] = useState(null);
  const headerImgRef = useRef(null); // loaded HTMLImageElement

  // Load header image into an Image object when src changes
  useEffect(() => {
    if (!headerImageSrc) { headerImgRef.current = null; return; }
    const img = new Image();
    img.onload = () => { headerImgRef.current = img; };
    img.src = headerImageSrc;
  }, [headerImageSrc]);

  const handleHeaderImageFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = e => setHeaderImageSrc(e.target.result);
    reader.readAsDataURL(file);
  };

  const canvasRef   = useRef(null);
  const rafRef      = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef   = useRef([]);
  const startTsRef  = useRef(null);

  // Chart mode — must be before config
  const [chartMode, setChartMode] = useState("line"); // "line" | "candles"

  const config = {
    W: 1080, H: 800,
    ticker:     selectedAsset.ticker === "CUSTOM" ? customTicker : selectedAsset.ticker,
    assetName:  selectedAsset.ticker === "CUSTOM" ? customTicker : selectedAsset.name,
    assetColor: selectedAsset.ticker === "CUSTOM" ? customColor  : selectedAsset.color,
    assetIcon:  selectedAsset.ticker === "CUSTOM" ? customIcon   : selectedAsset.icon,
    startPrice, endPrice, label, headlineText,
    bgColor, headlineColor, subtextColor, counterColor,
    headerImg: headerImgRef,
    chartMode,
  };

  // Fetch real 24h price history
  const sparkBaseRef  = useRef(null);
  const candleBaseRef = useRef(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError,   setPriceError]   = useState(null);

  // Data ranges — what period to fetch
  const DATA_RANGES = [
    { id:"5h",  label:"5h",       range:"5h"  },
    { id:"12h", label:"12h",      range:"12h" },
    { id:"1d",  label:"24h",      range:"1d"  },
    { id:"2d",  label:"2d",       range:"2d"  },
    { id:"5d",  label:"5d",       range:"5d"  },
    { id:"1mo", label:"1 month",  range:"1mo" },
    { id:"3mo", label:"3 months", range:"3mo" },
    { id:"6mo", label:"6 months", range:"6mo" },
    { id:"1y",  label:"1 year",   range:"1y"  },
  ];

  // Candle sizes — grouping of raw bars
  // Yahoo max resolution per range:
  // ≤2d → 1m/2m/5m/15m/30m/60m/90m; ≤1mo → up to 1d; else 1wk/1mo
  const CANDLE_SIZES = [
    { id:"1m",  label:"1 min",    minutes:1   },
    { id:"5m",  label:"5 min",    minutes:5   },
    { id:"15m", label:"15 min",   minutes:15  },
    { id:"30m", label:"30 min",   minutes:30  },
    { id:"1h",  label:"1 hour",   minutes:60  },
    { id:"4h",  label:"4 hours",  minutes:240 },
    { id:"1d",  label:"1 day",    minutes:1440},
    { id:"1wk", label:"1 week",   minutes:10080},
  ];

  const [dataRange,   setDataRange]   = useState("1d");
  const [candleSize,  setCandleSize]  = useState("1h");
  const [priceSource, setPriceSource] = useState("custom");
  const selectedDataRange = DATA_RANGES.find(r => r.id === dataRange) || DATA_RANGES[2];
  const selectedCandleSize = CANDLE_SIZES.find(c => c.id === candleSize) || CANDLE_SIZES[4];

  // Determine best fetch interval for the chosen candle size + range
  const getFetchInterval = (candleMins, range) => {
    // Yahoo supported intervals per range
    if (["5h","12h","1d","2d"].includes(range)) {
      if (candleMins <= 1)   return "1m";
      if (candleMins <= 5)   return "5m";
      if (candleMins <= 15)  return "15m";
      if (candleMins <= 30)  return "30m";
      if (candleMins <= 60)  return "60m";
      if (candleMins <= 90)  return "90m";
      return "1d";
    }
    if (["5d","1mo","3mo"].includes(range)) {
      if (candleMins <= 5)   return "5m";
      if (candleMins <= 30)  return "30m";
      if (candleMins <= 60)  return "60m";
      return "1d";
    }
    return "1d"; // 6mo, 1y
  };

  // When switching to real prices, store fetched first/last for counter animation
  const realPriceRangeRef = useRef({ start: null, end: null });
  const [realPriceData, setRealPriceData] = useState(null); // { start, end, high, low, candles } for UI display

  useEffect(() => {
    // Clear old data
    sparkBaseRef.current  = null;
    candleBaseRef.current = null;
    realPriceRangeRef.current = { start: null, end: null };
    setRealPriceData(null);

    const yahooTicker = selectedAsset.ticker === "CUSTOM"
      ? (customTicker.includes("-") ? customTicker : `${customTicker}-USD`)
      : selectedAsset.yahooTicker;

    // Custom asset → always use synthetic data
    if (!yahooTicker || selectedAsset.ticker === "CUSTOM") {
      const pts = [];
      let v = startPrice;
      const step = (endPrice - startPrice) / 59;
      for (let i = 0; i < 60; i++) {
        v += step + (Math.random() - 0.5) * Math.abs(endPrice - startPrice) * 0.03;
        pts.push(v);
      }
      pts[pts.length - 1] = endPrice;
      sparkBaseRef.current = pts;
      const groupSize = Math.max(2, Math.floor(pts.length / 20));
      const candles = [];
      for (let i = 0; i < pts.length; i += groupSize) {
        const group = pts.slice(i, i + groupSize);
        if (!group.length) continue;
        const o = group[0], c = group[group.length - 1];
        const h = Math.max(...group) * (1 + Math.random() * 0.003);
        const l = Math.min(...group) * (1 - Math.random() * 0.003);
        candles.push({ o, h, l, c });
      }
      candleBaseRef.current = candles;
      return;
    }

    // Real asset → fetch via our own Vercel API (server-side, no CORS)
    setPriceLoading(true);
    setPriceError(null);

    const fetchInterval = getFetchInterval(selectedCandleSize.minutes, dataRange);

    const tryFetch = async () => {
      const res = await fetch(
        `/api/price-history?ticker=${encodeURIComponent(yahooTicker)}&interval=${fetchInterval}&range=${selectedDataRange.range}&t=${Date.now()}`,
        { signal: AbortSignal.timeout(10000) }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      return json;
    };

    tryFetch().then(json => {
      const result = json?.chart?.result?.[0];
      if (!result) { setPriceError("No data returned"); setPriceLoading(false); return; }

      const q      = result.indicators?.quote?.[0] || {};
      const closes = q.close  || [];
      const opens  = q.open   || [];
      const highs  = q.high   || [];
      const lows   = q.low    || [];
      const valid  = closes.filter(v => v != null && v > 0);

      if (valid.length < 2) { setPriceError("Not enough data"); setPriceLoading(false); return; }

      sparkBaseRef.current = valid;
      realPriceRangeRef.current = { start: valid[0], end: valid[valid.length - 1] };
      setRealPriceData({
        start:   valid[0],
        end:     valid[valid.length - 1],
        high:    Math.max(...highs.filter(v => v != null && v > 0)),
        low:     Math.min(...lows.filter(v  => v != null && v > 0)),
        candles: 0,
      });

      // Group bars into desired candle size
      const fetchMins = (() => {
        const m = { "1m":1,"2m":2,"5m":5,"15m":15,"30m":30,"60m":60,"90m":90,"1h":60,"1d":1440,"1wk":10080 };
        return m[fetchInterval] || 60;
      })();
      const groupSize = Math.max(1, Math.round(selectedCandleSize.minutes / fetchMins));

      const candles = [];
      for (let i = 0; i < closes.length; i += groupSize) {
        const slice = { o:[], h:[], l:[], c:[] };
        for (let j = 0; j < groupSize && i + j < closes.length; j++) {
          const idx = i + j;
          if (opens[idx]  != null) slice.o.push(opens[idx]);
          if (highs[idx]  != null) slice.h.push(highs[idx]);
          if (lows[idx]   != null) slice.l.push(lows[idx]);
          if (closes[idx] != null) slice.c.push(closes[idx]);
        }
        if (!slice.o.length || !slice.c.length) continue;
        candles.push({
          o: slice.o[0],
          h: Math.max(...slice.h),
          l: Math.min(...slice.l),
          c: slice.c[slice.c.length - 1],
        });
      }

      candleBaseRef.current = candles.length > 2 ? candles : null;
      setRealPriceData(prev => prev ? { ...prev, candles: candles.length } : prev);
      setPriceError(null);
      setPriceLoading(false);
    }).catch(err => {
      setPriceError(`Fetch failed: ${err.message}`);
      setPriceLoading(false);
    });

  }, [selectedAsset, customTicker, dataRange, candleSize]);

  const animate = useCallback((recording = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    startTsRef.current = null;
    setIsPlaying(true);

    // Init particles
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * config.W, y: Math.random() * config.H,
      vx: (Math.random() - 0.5) * 4, vy: -(Math.random() * 6 + 2),
      r: Math.random() * 5 + 2,
      color: [config.assetColor, C.green, "#fff", "#fbbf24"][Math.floor(Math.random() * 4)],
      opacity: 0,
    }));

    const totalMs = duration * 1000;

    const frame = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts;
      const elapsed = ts - startTsRef.current;
      const t = Math.min(elapsed / totalMs, 1);
      setProgress(t);

      // Counter — use real prices or custom depending on priceSource
      const realRange   = realPriceRangeRef.current;
      const animStart   = (priceSource === "real" && realRange.start != null) ? realRange.start : startPrice;
      const animEnd     = (priceSource === "real" && realRange.end   != null) ? realRange.end   : endPrice;
      const counterT    = easeInOutCubic(Math.min(t / 0.85, 1));
      const counterValue = animStart + (animEnd - animStart) * counterT;

      // Sparkline — grow with animation
      const sparkAll = sparkBaseRef.current && sparkBaseRef.current.length > 2
        ? sparkBaseRef.current
        : Array.from({ length: 60 }, (_, i) => {
            const pct = i / 59;
            return animStart + (animEnd - animStart) * pct * 0.7
              + Math.sin(i * 0.8) * Math.abs(animEnd - animStart) * 0.04;
          });
      const sparkCount  = Math.max(2, Math.floor(t * sparkAll.length));
      const sparkPoints = [...sparkAll.slice(0, sparkCount - 1), counterValue];

      // Candles — grow one by one
      const candleAll = candleBaseRef.current && candleBaseRef.current.length > 3
        ? candleBaseRef.current
        : Array.from({ length: 30 }, (_, i) => {
            const pct = i / 29;
            const mid = animStart + (animEnd - animStart) * pct * 0.7;
            const range = Math.abs(animEnd - animStart) * 0.015;
            const o = mid + (Math.random() - 0.5) * range;
            const c = mid + (Math.random() - 0.5) * range + (animEnd > animStart ? range * 0.3 : -range * 0.3);
            return { o, h: Math.max(o, c) + range * 0.5, l: Math.min(o, c) - range * 0.5, c };
          });
      // How many candles visible + partial progress of last one
      const totalCandles    = candleAll.length;
      const candleFloatIdx  = t * totalCandles;
      const candleCount     = Math.max(1, Math.floor(candleFloatIdx));
      const lastCandlePct   = candleFloatIdx - Math.floor(candleFloatIdx); // 0..1 grow progress of current candle
      const visibleCandles  = candleAll.slice(0, candleCount);
      // Last candle overrides close to follow counter
      if (visibleCandles.length > 0) {
        const last = { ...visibleCandles[visibleCandles.length - 1], c: counterValue };
        visibleCandles[visibleCandles.length - 1] = last;
      }

      // Label pop — appears after 80%
      const labelT     = Math.max(0, (t - 0.8) / 0.2);
      const labelScale = labelT < 0.5 ? 1 + Math.sin(labelT * Math.PI) * 0.3 : 1;
      const labelOpacity = easeOutExpo(labelT);

      // Glow — ramps up
      const glowOpacity = Math.min(t / 0.5, 1);

      // Particles — activate after 80%, keep looping during main animation
      if (t > 0.8) {
        particles.forEach(p => {
          p.opacity = Math.min(p.opacity + 0.04, 0.9);
          p.x += p.vx; p.y += p.vy;
          p.vy += 0.15;
          // During main animation — respawn. After t=1 — let them fall off
          if (t < 1 && p.y > config.H + 20) {
            p.y = config.H + 10; p.x = Math.random() * config.W; p.vy = -(Math.random() * 6 + 2);
          }
        });
      }

      drawFrame(ctx, { progress:t, counterValue, sparkPoints, particles, labelScale, labelOpacity, glowOpacity, candles: visibleCandles, candleAll, candleFloatIdx, lastCandlePct, lineRevealPct: easeInOutCubic(Math.min(t / 0.85, 1)), sparkAll }, config);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        // ── Phase 2: particles fall off screen ───────────────────────────────
        const fallPhase = (ts2) => {
          const allGone = particles.every(p => p.y > config.H + 30 || p.opacity <= 0);

          particles.forEach(p => {
            if (p.y <= config.H + 30) {
              p.x += p.vx; p.y += p.vy;
              p.vy += 0.2; // slightly more gravity when falling off
            }
          });

          drawFrame(ctx, {
            progress: 1, counterValue: animEnd,
            sparkPoints: [...sparkAll.slice(0, sparkAll.length - 1), animEnd],
            particles, labelScale: 1, labelOpacity: 1, glowOpacity: 1,
            candles: visibleCandles, candleAll, candleFloatIdx: candleAll.length, lastCandlePct: 1,
            lineRevealPct: 1, sparkAll,
          }, config);

          if (!allGone) {
            rafRef.current = requestAnimationFrame(fallPhase);
          } else {
            setProgress(1);
            drawFrame(ctx, {
              progress: 1, counterValue: animEnd,
              sparkPoints: [...sparkAll.slice(0, sparkAll.length - 1), animEnd],
              particles: [], labelScale: 1, labelOpacity: 1, glowOpacity: 1,
              candles: visibleCandles, candleAll, candleFloatIdx: candleAll.length, lastCandlePct: 1,
              lineRevealPct: 1, sparkAll,
            }, config);
            setTimeout(() => {
              setIsPlaying(false);
              if (recording) stopRecording();
            }, 5000);
          }
        };
        rafRef.current = requestAnimationFrame(fallPhase);
      }
    };

    rafRef.current = requestAnimationFrame(frame);
  }, [config, duration]);

  const [converting, setConverting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null); // null | "recording" | "done" | "error"

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const recordAndExport = (format) => {
    const canvas = canvasRef.current;
    if (!canvas || isRecording || isPlaying || converting) return;

    cancelAnimationFrame(rafRef.current);
    chunksRef.current = [];
    setExportStatus("recording");

    // Pick best supported mimeType
    const mp4Types  = ["video/mp4;codecs=avc1","video/mp4"];
    const webmTypes = ["video/webm;codecs=vp9","video/webm;codecs=vp8","video/webm"];
    const tryTypes  = format === "mp4" ? [...mp4Types, ...webmTypes] : webmTypes;
    const mimeType  = tryTypes.find(t => MediaRecorder.isTypeSupported(t)) || "video/webm";
    const actualFmt = mimeType.startsWith("video/mp4") ? "mp4" : "webm";

    const stream   = canvas.captureStream(30);
    const recorder = new MediaRecorder(stream, { mimeType });
    recorderRef.current = recorder;

    recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `${config.ticker}-${config.endPrice}.${actualFmt}`;
      a.click();
      URL.revokeObjectURL(url);
      setIsRecording(false);
      setExportStatus(actualFmt === format ? "done" : "fallback");
      setTimeout(() => setExportStatus(null), 4000);
    };

    recorder.start();
    setIsRecording(true);
    animate(true);
  };

  const handlePreview = () => {
    cancelAnimationFrame(rafRef.current);
    setProgress(0);
    animate(false);
  };

  // Draw static preview on config change — immediate, no transition
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || isPlaying) return;
    const ctx = canvas.getContext("2d");

    const animEnd = (priceSource === "real" && realPriceRangeRef.current.end != null)
      ? realPriceRangeRef.current.end : endPrice;
    const animStart = (priceSource === "real" && realPriceRangeRef.current.start != null)
      ? realPriceRangeRef.current.start : startPrice;

    const base = sparkBaseRef.current;
    const previewPoints = base && base.length > 1
      ? base
      : Array.from({ length: 40 }, (_, i) => {
          const t = i / 39;
          return animStart + (animEnd - animStart) * t * 0.6 + Math.sin(i * 0.5) * (animEnd - animStart) * 0.05;
        });

    const allCandles = candleBaseRef.current && candleBaseRef.current.length > 3
      ? candleBaseRef.current
      : Array.from({ length: 30 }, (_, i) => {
          const pct = i / 29;
          const mid = animStart + (animEnd - animStart) * pct * 0.7;
          const range = Math.abs(animEnd - animStart) * 0.015 || mid * 0.01;
          const o = mid + (Math.random() - 0.5) * range;
          const c = mid + (Math.random() - 0.5) * range + (animEnd > animStart ? range * 0.3 : -range * 0.3);
          return { o, h: Math.max(o, c) + range * 0.5, l: Math.min(o, c) - range * 0.5, c };
        });

    // Draw immediately — skip animation by setting progress:1 and all revealed
    drawFrame(ctx, {
      progress: 1, counterValue: animEnd,
      sparkPoints: previewPoints,
      particles: [], labelScale: 1, labelOpacity: 1, glowOpacity: 1,
      candles: allCandles, candleAll: allCandles, candleFloatIdx: allCandles.length, lastCandlePct: 1,
      lineRevealPct: 1, sparkAll: previewPoints,
    }, config);
  }, [
    selectedAsset, startPrice, endPrice, label, customTicker, customColor, customIcon,
    headlineText, bgColor, headlineColor, subtextColor, counterColor,
    headerImageSrc, chartMode, priceSource, dataRange, candleSize,
  ]);

  const inputStyle = {
    width:"100%", background:"var(--bg3)", border:"1px solid var(--border)",
    borderRadius:8, padding:"9px 12px",
    fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700, color:"var(--text)",
    outline:"none", boxSizing:"border-box",
  };

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24, alignItems:"start" }}>

      {/* ── LEFT: Canvas preview ── */}
      <div>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:10 }}>
          Preview — 1080×1080
        </div>
        <div style={{ position:"relative", width:"100%", aspectRatio:"1080/800", borderRadius:12, overflow:"hidden", border:"1px solid var(--border)" }}>
          <canvas
            ref={canvasRef}
            width={1080} height={800}
            style={{ width:"100%", height:"100%", display:"block" }}
          />
          {/* Progress bar */}
          {isPlaying && (
            <div style={{ position:"absolute", bottom:0, left:0, right:0, height:3, background:"var(--border)" }}>
              <div style={{ height:"100%", width:`${progress*100}%`, background:config.assetColor, transition:"width 0.05s linear" }}/>
            </div>
          )}
          {/* Live data badge */}
          <div style={{ position:"absolute", top:8, right:8, display:"flex", alignItems:"center", gap:5,
            background:"rgba(0,0,0,0.7)", borderRadius:6, padding:"4px 8px",
            border:`1px solid ${priceError ? "#f87171" : priceLoading ? "var(--border)" : "#4ade80"}33` }}>
            <div style={{ width:5, height:5, borderRadius:"50%",
              background: priceError ? "#f87171" : priceLoading ? C.orange : "#4ade80",
              boxShadow: priceLoading ? "none" : `0 0 4px ${priceError ? "#f87171" : "#4ade80"}`,
            }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
              color: priceError ? "#f87171" : priceLoading ? C.orange : "#4ade80",
              letterSpacing:"0.06em", textTransform:"uppercase" }}>
              {priceError ? "Simulated" : priceLoading ? "Loading…" : "Live 24h"}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display:"flex", gap:8, marginTop:12, flexWrap:"wrap" }}>
          {/* Preview */}
          <button onClick={handlePreview} disabled={isPlaying || isRecording || converting || priceLoading} style={{
            flex:1, minWidth:80, background:"var(--bg3)", border:`1px solid ${priceLoading ? C.orange : "var(--border2)"}`,
            borderRadius:8, padding:"11px 8px", cursor:(isPlaying||isRecording||converting||priceLoading)?"not-allowed":"pointer",
            fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color: priceLoading ? C.orange : "var(--text3)",
            display:"flex", alignItems:"center", justifyContent:"center", gap:6,
            opacity:(isPlaying||isRecording||converting)?0.4:1,
            transition:"all 0.15s",
          }}>
            <i className={`ph ${priceLoading ? "ph-circle-notch" : "ph-play"}`}
              style={{ fontSize:15, ...(priceLoading ? { animation:"spin 1s linear infinite" } : {}) }}/>
            {priceLoading ? "Loading data…" : "Preview"}
          </button>

          {/* MP4 */}
          <button onClick={() => recordAndExport("mp4")} disabled={isPlaying||isRecording||converting} style={{
            flex:2, minWidth:120,
            background:"rgba(96,165,250,0.12)", border:"1px solid #60a5fa88",
            borderRadius:8, padding:"11px 8px",
            cursor:(isPlaying||isRecording||converting||priceLoading)?"not-allowed":"pointer",
            fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"#60a5fa",
            display:"flex", alignItems:"center", justifyContent:"center", gap:6,
            opacity:(isPlaying||isRecording||converting||priceLoading)?0.5:1,
          }}>
            <i className={`ph ${isRecording?"ph-record":"ph-file-video"}`} style={{ fontSize:15 }}/>
            {isRecording ? "Recording…" : "↓ MP4"}
          </button>

          {/* WEBM */}
          <button onClick={() => recordAndExport("webm")} disabled={isPlaying||isRecording||converting||priceLoading} style={{
            flex:1, minWidth:80,
            background:`${config.assetColor}12`, border:`1px solid ${config.assetColor}55`,
            borderRadius:8, padding:"11px 8px",
            cursor:(isPlaying||isRecording||converting||priceLoading)?"not-allowed":"pointer",
            fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:config.assetColor,
            display:"flex", alignItems:"center", justifyContent:"center", gap:6,
            opacity:(isPlaying||isRecording||converting||priceLoading)?0.4:1,
          }}>
            <i className="ph ph-download-simple" style={{ fontSize:15 }}/>
            WEBM
          </button>
        </div>

        {/* Status message */}
        {exportStatus === "done" && (
          <div style={{ marginTop:6, fontFamily:"Montserrat,sans-serif", fontSize:10, color:"#4ade80", textAlign:"center" }}>
            ✓ Downloaded successfully
          </div>
        )}
        {exportStatus === "fallback" && (
          <div style={{ marginTop:6, fontFamily:"Montserrat,sans-serif", fontSize:10, color:C.orange, textAlign:"center" }}>
            ⚠ MP4 not supported in this browser — saved as .webm instead.<br/>
            <span style={{ color:"var(--text4)" }}>X accepts .webm · rename to .mp4 if needed</span>
          </div>
        )}
        {!exportStatus && (
          <div style={{ marginTop:6, fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)", textAlign:"center" }}>
            MP4 за X/Instagram · WEBM е по-бърз fallback
          </div>
        )}
      </div>

      {/* ── RIGHT: Controls ── */}
      <div style={{ display:"flex", flexDirection:"column", gap:14, maxHeight:"90vh", overflowY:"auto", paddingRight:4 }}>

        {/* Asset selector */}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>Asset</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {ASSETS.map(a => (
              <button key={a.ticker} onClick={() => setSelectedAsset(a)} style={{
                background: selectedAsset.ticker === a.ticker ? `${a.color}22` : "var(--bg3)",
                border:`1px solid ${selectedAsset.ticker === a.ticker ? a.color : "var(--border)"}`,
                borderRadius:8, padding:"6px 12px", cursor:"pointer", transition:"all 0.15s",
                fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
                color: selectedAsset.ticker === a.ticker ? a.color : "var(--text3)",
              }}>{a.ticker}</button>
            ))}
          </div>
          {selectedAsset.ticker === "CUSTOM" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 60px", gap:8, marginTop:10 }}>
              <div>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", marginBottom:4 }}>TICKER</div>
                <input value={customTicker} onChange={e => setCustomTicker(e.target.value.toUpperCase())} style={inputStyle} maxLength={6}/>
              </div>
              <div>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", marginBottom:4 }}>ICON</div>
                <input value={customIcon} onChange={e => setCustomIcon(e.target.value)} style={inputStyle} maxLength={2}/>
              </div>
              <div>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", marginBottom:4 }}>COLOR</div>
                <input type="color" value={customColor} onChange={e => setCustomColor(e.target.value)}
                  style={{ ...inputStyle, padding:"4px", height:38, cursor:"pointer" }}/>
              </div>
            </div>
          )}
        </div>

        {/* Live price info card — always visible for real assets */}
        {selectedAsset.ticker !== "CUSTOM" && (
          <div style={{
            background:"var(--bg2)",
            border:`1px solid ${priceLoading ? "var(--border)" : priceError ? "#f87171" : "#4ade8044"}`,
            borderRadius:12, padding:"14px 16px", transition:"border-color 0.3s",
          }}>
            {priceLoading ? (
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <i className="ph ph-circle-notch" style={{ fontSize:14, color:C.orange, animation:"spin 1s linear infinite" }}/>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"var(--text4)" }}>
                  Fetching {selectedAsset.name} · {selectedDataRange.label} · {selectedCandleSize.label} candles…
                </span>
              </div>
            ) : priceError ? (
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"#f87171" }}>⚠ {priceError}</div>
            ) : realPriceData ? (() => {
              const isUp  = realPriceData.end >= realPriceData.start;
              const chg   = ((realPriceData.end - realPriceData.start) / realPriceData.start * 100).toFixed(2);
              const color = isUp ? "#4ade80" : "#f87171";
              return (
                <div>
                  {/* Header */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:6, height:6, borderRadius:"50%", background:"#4ade80", boxShadow:"0 0 6px #4ade80" }}/>
                      <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color:"var(--text3)", letterSpacing:"0.06em" }}>
                        {selectedAsset.name} · {selectedDataRange.label}
                      </span>
                    </div>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
                      color, background:`${color}18`, padding:"2px 8px", borderRadius:5 }}>
                      {isUp ? "+" : ""}{chg}%
                    </span>
                  </div>
                  {/* Price grid */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8 }}>
                    {[
                      { label:"OPEN",  value:fmtFull(realPriceData.start), color:"var(--text3)" },
                      { label:"CLOSE", value:fmtFull(realPriceData.end),   color },
                      { label:"HIGH",  value:fmtFull(realPriceData.high),  color:"#4ade80" },
                      { label:"LOW",   value:fmtFull(realPriceData.low),   color:"#f87171" },
                    ].map(s => (
                      <div key={s.label} style={{ background:"var(--bg3)", borderRadius:8, padding:"8px 10px" }}>
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, color:"var(--text4)", letterSpacing:"0.08em", marginBottom:4 }}>{s.label}</div>
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:s.color }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                  {/* Candle count */}
                  <div style={{ marginTop:8, fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)" }}>
                    {realPriceData.candles} candles · {selectedCandleSize.label} each
                  </div>
                </div>
              );
            })() : null}
          </div>
        )}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>Price</div>

          {/* Source toggle */}
          <div style={{ display:"flex", gap:6, marginBottom:12 }}>
            {[
              { id:"real",   label:"Real prices",   icon:"ph-chart-line-up" },
              { id:"custom", label:"Custom range",  icon:"ph-pencil-simple" },
            ].map(s => (
              <button key={s.id} onClick={() => setPriceSource(s.id)} style={{
                flex:1, background: priceSource === s.id ? `${config.assetColor}18` : "var(--bg3)",
                border:`1px solid ${priceSource === s.id ? config.assetColor : "var(--border)"}`,
                borderRadius:8, padding:"8px 6px", cursor:"pointer", transition:"all 0.15s",
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              }}>
                <i className={`ph ${s.icon}`} style={{ fontSize:13, color: priceSource === s.id ? config.assetColor : "var(--text4)" }}/>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700,
                  color: priceSource === s.id ? config.assetColor : "var(--text4)" }}>{s.label}</span>
              </button>
            ))}
          </div>

          {/* Real prices info */}
          {priceSource === "real" && (
            <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8, padding:"10px 12px" }}>
              {realPriceRangeRef.current.start != null ? (
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", marginBottom:3 }}>RANGE</div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700, color:"var(--text)" }}>
                      {fmtFull(realPriceRangeRef.current.start)}
                      <span style={{ color:"var(--text4)", margin:"0 6px" }}>→</span>
                      {fmtFull(realPriceRangeRef.current.end)}
                    </div>
                  </div>
                  <div style={{
                    fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, padding:"4px 10px", borderRadius:6,
                    color: realPriceRangeRef.current.end >= realPriceRangeRef.current.start ? "#4ade80" : "#f87171",
                    background: realPriceRangeRef.current.end >= realPriceRangeRef.current.start ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)",
                  }}>
                    {realPriceRangeRef.current.end >= realPriceRangeRef.current.start ? "+" : ""}
                    {(((realPriceRangeRef.current.end - realPriceRangeRef.current.start) / realPriceRangeRef.current.start) * 100).toFixed(2)}%
                  </div>
                </div>
              ) : (
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"var(--text4)" }}>
                  {priceLoading ? "Loading prices…" : "Select an asset and time range above"}
                </div>
              )}
            </div>
          )}

          {/* Custom inputs */}
          {priceSource === "custom" && (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {[
                  { label:"Start Price", value:startPrice, set:setStartPrice },
                  { label:"End Price",   value:endPrice,   set:setEndPrice   },
                ].map(f => (
                  <div key={f.label}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", marginBottom:4, letterSpacing:"0.06em" }}>{f.label.toUpperCase()}</div>
                    <div style={{ position:"relative" }}>
                      <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700, color:"var(--text3)" }}>$</span>
                      <input type="number" value={f.value} onChange={e => f.set(Number(e.target.value))}
                        style={{ ...inputStyle, paddingLeft:24 }}
                        onFocus={e => e.currentTarget.style.borderColor = config.assetColor}
                        onBlur={e  => e.currentTarget.style.borderColor = "var(--border)"}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {endPrice > startPrice && (
                <div style={{ marginTop:8, fontFamily:"Montserrat,sans-serif", fontSize:11, color:C.green }}>
                  +{((endPrice - startPrice) / startPrice * 100).toFixed(2)}% · {`$${(endPrice - startPrice).toLocaleString()}`} move
                </div>
              )}
            </>
          )}
        </div>

        {/* Headline text */}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>Headline Text</div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)", marginBottom:8 }}>
            Shows as: <span style={{ color:"#fff", fontWeight:700 }}>{config.assetName}</span> <span style={{ color:config.assetColor, fontWeight:700 }}>{headlineText}</span>
          </div>
          <input value={headlineText} onChange={e => setHeadlineText(e.target.value)}
            placeholder="just reached" style={inputStyle} maxLength={40}
            onFocus={e => e.currentTarget.style.borderColor = config.assetColor}
            onBlur={e  => e.currentTarget.style.borderColor = "var(--border)"}
          />
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
            {["just reached", "hit a new ATH of", "is now at", "just pumped to", "broke"].map(t => (
              <button key={t} onClick={() => setHeadlineText(t)} style={{
                background: headlineText === t ? `${config.assetColor}22` : "var(--bg3)",
                border:`1px solid ${headlineText === t ? config.assetColor : "var(--border)"}`,
                borderRadius:6, padding:"4px 10px", cursor:"pointer",
                fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:600,
                color: headlineText === t ? config.assetColor : "var(--text4)",
              }}>{t}</button>
            ))}
          </div>
        </div>

        {/* Label */}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>Milestone Badge</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
            {LABELS.map(l => (
              <button key={l} onClick={() => setLabel(l)} style={{
                background: label === l ? `${config.assetColor}22` : "var(--bg3)",
                border:`1px solid ${label === l ? config.assetColor : "var(--border)"}`,
                borderRadius:8, padding:"6px 12px", cursor:"pointer",
                fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
                color: label === l ? config.assetColor : "var(--text3)",
              }}>{l}</button>
            ))}
          </div>
          <input value={label} onChange={e => setLabel(e.target.value)}
            placeholder="Custom badge…" style={inputStyle} maxLength={30}
            onFocus={e => e.currentTarget.style.borderColor = config.assetColor}
            onBlur={e  => e.currentTarget.style.borderColor = "var(--border)"}
          />
        </div>

        {/* Chart Type */}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>Chart Type</div>
          <div style={{ display:"flex", gap:8 }}>
            {[
              { id:"line",    label:"Line",    icon:"ph-chart-line-up" },
              { id:"candles", label:"Candles", icon:"ph-chart-candlestick" },
            ].map(m => (
              <button key={m.id} onClick={() => setChartMode(m.id)} style={{
                flex:1, background: chartMode === m.id ? `${config.assetColor}18` : "var(--bg3)",
                border:`1px solid ${chartMode === m.id ? config.assetColor : "var(--border)"}`,
                borderRadius:8, padding:"10px 8px", cursor:"pointer", transition:"all 0.15s",
                display:"flex", alignItems:"center", justifyContent:"center", gap:7,
              }}>
                <i className={`ph ${m.icon}`} style={{ fontSize:16, color: chartMode === m.id ? config.assetColor : "var(--text4)" }}/>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700,
                  color: chartMode === m.id ? config.assetColor : "var(--text4)" }}>{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Data Range */}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase" }}>Data Range</div>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9,
              color: priceLoading ? C.orange : priceError ? "#f87171" : selectedAsset.ticker === "CUSTOM" ? "var(--text4)" : "#4ade80",
              fontWeight:700 }}>
              {priceLoading ? "Loading…" : priceError ? "Simulated" : selectedAsset.ticker === "CUSTOM" ? "Custom asset" : "● Live"}
            </span>
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {DATA_RANGES.map(r => (
              <button key={r.id} onClick={() => setDataRange(r.id)} style={{
                background: dataRange === r.id ? `${config.assetColor}18` : "var(--bg3)",
                border:`1px solid ${dataRange === r.id ? config.assetColor : "var(--border)"}`,
                borderRadius:6, padding:"5px 10px", cursor:"pointer", transition:"all 0.15s",
                fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700,
                color: dataRange === r.id ? config.assetColor : "var(--text4)",
              }}>{r.label}</button>
            ))}
          </div>
        </div>

        {/* Candle Size */}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>
            Candle Size
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {CANDLE_SIZES.map(c => (
              <button key={c.id} onClick={() => setCandleSize(c.id)} style={{
                background: candleSize === c.id ? `${config.assetColor}18` : "var(--bg3)",
                border:`1px solid ${candleSize === c.id ? config.assetColor : "var(--border)"}`,
                borderRadius:6, padding:"5px 10px", cursor:"pointer", transition:"all 0.15s",
                fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700,
                color: candleSize === c.id ? config.assetColor : "var(--text4)",
              }}>{c.label}</button>
            ))}
          </div>
          <div style={{ marginTop:8, fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)" }}>
            {candleBaseRef.current
              ? `${candleBaseRef.current.length} candles loaded`
              : "Fetch interval auto-selected based on range + candle size"}
          </div>
        </div>

        {/* Duration */}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>Duration</div>
          <div style={{ position:"relative", display:"flex", alignItems:"center" }}>
            <input
              type="number" value={duration} min={2} max={30} step={1}
              onChange={e => setDuration(Math.max(2, Math.min(30, Number(e.target.value))))}
              style={{ ...inputStyle, paddingRight:36, WebkitAppearance:"none", MozAppearance:"textfield" }}
              onFocus={e => e.currentTarget.style.borderColor = config.assetColor}
              onBlur={e  => e.currentTarget.style.borderColor = "var(--border)"}
            />
            <span style={{ position:"absolute", right:12, fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color:"var(--text4)", pointerEvents:"none" }}>sec</span>
          </div>
          <div style={{ display:"flex", gap:6, marginTop:8 }}>
            {[3, 5, 8, 10, 15].map(s => (
              <button key={s} onClick={() => setDuration(s)} style={{
                flex:1, background: duration === s ? `${config.assetColor}18` : "var(--bg3)",
                border:`1px solid ${duration === s ? config.assetColor : "var(--border)"}`,
                borderRadius:6, padding:"5px 0", cursor:"pointer",
                fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700,
                color: duration === s ? config.assetColor : "var(--text4)",
              }}>{s}s</button>
            ))}
          </div>
          <div style={{ marginTop:8, fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)" }}>
            + 3s freeze on last frame
          </div>
        </div>

        {/* Header Image */}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:12 }}>Header Image</div>

          {/* Drop zone / preview */}
          <div
            onClick={() => document.getElementById("headerImgInput").click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = config.assetColor; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; }}
            onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = "var(--border)"; const f = e.dataTransfer.files[0]; if (f) handleHeaderImageFile(f); }}
            style={{
              width:"100%", height: headerImageSrc ? "auto" : 80,
              border:`1px dashed ${headerImageSrc ? config.assetColor : "var(--border)"}`,
              borderRadius:10, cursor:"pointer", overflow:"hidden",
              display:"flex", alignItems:"center", justifyContent:"center",
              background:"var(--bg3)", transition:"border-color 0.15s",
            }}
          >
            {headerImageSrc ? (
              <img src={headerImageSrc} alt="header" style={{ width:"100%", height:80, objectFit:"cover", display:"block" }}/>
            ) : (
              <div style={{ textAlign:"center" }}>
                <i className="ph ph-image" style={{ fontSize:22, color:"var(--text4)", display:"block", marginBottom:4 }}/>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)" }}>Click or drag image here</span>
              </div>
            )}
          </div>
          <input id="headerImgInput" type="file" accept="image/*" style={{ display:"none" }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleHeaderImageFile(f); e.target.value = ""; }}
          />

          {headerImageSrc && (
            <button onClick={() => setHeaderImageSrc(null)} style={{
              marginTop:8, width:"100%", background:"transparent",
              border:"1px solid var(--border)", borderRadius:8, padding:"6px",
              cursor:"pointer", fontFamily:"Montserrat,sans-serif", fontSize:10,
              fontWeight:700, color:"var(--text4)",
            }}>Remove image</button>
          )}
          <div style={{ marginTop:8, fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)" }}>
            Renders as 1080×130px banner strip at top · without image shows gradient
          </div>
        </div>

        {/* Colors */}
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:12 }}>Colors</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[
              { label:"Background",  value:bgColor,       set:setBgColor,       hint:"#0a0a0a" },
              { label:"Asset Name",  value:headlineColor,  set:setHeadlineColor, hint:"#ffffff" },
              { label:"Subtext",     value:subtextColor,   set:setSubtextColor,  hint:config.assetColor },
              { label:"Counter",     value:counterColor,   set:setCounterColor,  hint:"#ffffff" },
            ].map(c => (
              <div key={c.label}>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", marginBottom:6, letterSpacing:"0.06em", textTransform:"uppercase" }}>{c.label}</div>
                <div style={{ display:"flex", alignItems:"center", gap:8, background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8, padding:"6px 10px" }}>
                  <input type="color" value={c.value} onChange={e => c.set(e.target.value)}
                    style={{ width:28, height:28, border:"none", borderRadius:6, cursor:"pointer", background:"none", padding:0 }}
                  />
                  <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text3)" }}>{c.value}</span>
                </div>
              </div>
            ))}
          </div>
          {/* Reset */}
          <button onClick={() => { setBgColor("#0a0a0a"); setHeadlineColor("#ffffff"); setSubtextColor(config.assetColor); setCounterColor("#ffffff"); }}
            style={{ marginTop:10, width:"100%", background:"transparent", border:"1px solid var(--border)", borderRadius:8,
              padding:"7px", cursor:"pointer", fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color:"var(--text4)" }}>
            Reset to defaults
          </button>
        </div>

      </div>
    </div>
  );
}
